import { lazy, Suspense, type ReactNode } from 'react'
import { createBrowserRouter, Navigate, Outlet, useLocation } from 'react-router'
import { useAuth } from '@/features/auth/auth-provider'
import { Spinner } from '@/components/ui/misc'
import { RouteError } from './route-error'

// Route-level code splitting: the public portal never downloads admin code.
const Landing = lazy(() => import('@/pages/landing-page'))
const SalonPage = lazy(() => import('@/pages/public/salon-page'))
const BookingPage = lazy(() => import('@/pages/booking/booking-page'))
const TrackingPage = lazy(() => import('@/pages/public/tracking-page'))
const PrivacyPage = lazy(() => import('@/pages/public/privacy-page'))
const NotFound = lazy(() => import('@/pages/not-found'))
const LoginPage = lazy(() => import('@/pages/auth/login-page'))
const SignupPage = lazy(() => import('@/pages/auth/signup-page'))
const ResetRequestPage = lazy(() => import('@/pages/auth/reset-request-page'))
const ResetPasswordPage = lazy(() => import('@/pages/auth/reset-password-page'))
const InvitationPage = lazy(() => import('@/pages/auth/invitation-page'))
const WorkspacePicker = lazy(() => import('@/pages/admin/workspace-picker'))
const CreateBusinessPage = lazy(() => import('@/pages/admin/create-business-page'))
const AdminLayout = lazy(() => import('@/pages/admin/admin-layout'))
const DashboardPage = lazy(() => import('@/pages/admin/dashboard-page'))
const RequestsPage = lazy(() => import('@/pages/admin/requests-page'))
const AgendaPage = lazy(() => import('@/pages/admin/agenda-page'))
const ServicesPage = lazy(() => import('@/pages/admin/services-page'))
const StaffPage = lazy(() => import('@/pages/admin/staff-page'))
const CustomersPage = lazy(() => import('@/pages/admin/customers-page'))
const SettingsPage = lazy(() => import('@/pages/admin/settings-page'))
const TeamPage = lazy(() => import('@/pages/admin/team-page'))
const OnboardingPage = lazy(() => import('@/pages/admin/onboarding-page'))
const PlatformPage = lazy(() => import('@/pages/platform/platform-page'))

function PageFallback() {
  return (
    <div className="grid min-h-[50dvh] place-items-center">
      <Spinner />
    </div>
  )
}

function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<PageFallback />}>{children}</Suspense>
}

function RequireAuth() {
  const { user, loading } = useAuth()
  const location = useLocation()
  if (loading) return <PageFallback />
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />
  return <Outlet />
}

export const router = createBrowserRouter([
  {
    errorElement: <RouteError />,
    children: [
      { path: '/', element: <Lazy><Landing /></Lazy> },
      { path: '/salon/:slug', element: <Lazy><SalonPage /></Lazy> },
      { path: '/salon/:slug/reservar', element: <Lazy><BookingPage /></Lazy> },
      { path: '/r/:token', element: <Lazy><TrackingPage /></Lazy> },
      { path: '/privacidad', element: <Lazy><PrivacyPage /></Lazy> },
      { path: '/login', element: <Lazy><LoginPage /></Lazy> },
      { path: '/registro', element: <Lazy><SignupPage /></Lazy> },
      { path: '/recuperar', element: <Lazy><ResetRequestPage /></Lazy> },
      { path: '/restablecer', element: <Lazy><ResetPasswordPage /></Lazy> },
      {
        element: <RequireAuth />,
        children: [
          { path: '/invitacion/:token', element: <Lazy><InvitationPage /></Lazy> },
          { path: '/app', element: <Lazy><WorkspacePicker /></Lazy> },
          { path: '/app/nuevo', element: <Lazy><CreateBusinessPage /></Lazy> },
          { path: '/plataforma', element: <Lazy><PlatformPage /></Lazy> },
          {
            path: '/app/:businessId',
            element: <Lazy><AdminLayout /></Lazy>,
            children: [
              { index: true, element: <Lazy><DashboardPage /></Lazy> },
              { path: 'solicitudes', element: <Lazy><RequestsPage /></Lazy> },
              { path: 'agenda', element: <Lazy><AgendaPage /></Lazy> },
              { path: 'servicios', element: <Lazy><ServicesPage /></Lazy> },
              { path: 'profesionales', element: <Lazy><StaffPage /></Lazy> },
              { path: 'clientas', element: <Lazy><CustomersPage /></Lazy> },
              { path: 'ajustes', element: <Lazy><SettingsPage /></Lazy> },
              { path: 'equipo', element: <Lazy><TeamPage /></Lazy> },
              { path: 'configuracion-inicial', element: <Lazy><OnboardingPage /></Lazy> },
            ],
          },
        ],
      },
      { path: '*', element: <Lazy><NotFound /></Lazy> },
    ],
  },
])
